import { headLinkCandidates } from '@felix/domain';
import { Button, Checkbox, Modal, Textarea } from '@mantine/core';
import { IconCheck, IconLink, IconMessage2Share, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import { allReportRows } from './all-reports.js';
import noon from './NoonReportScreen.module.css';
import { useReportLabels } from './report-labels.js';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyageReport, VoyageRequest } from './types.js';
import classes from './VoyageLinksPanels.module.css';
import record from './VoyageRecordScreens.module.css';
import { assignedVoyageIdOf } from './voyage-record.js';

/**
 * Sefer bağları ve ofisin sefer talepleri (ADR 0083; ortağın `sefer-3.jpeg`, `sefer-5.jpeg`):
 * "Voyage oluşturuldu → uygun raporlar bağlantı için önerilir". Gemide seçilen raporlar bağlanır;
 * ofiste aynı seçim gemiye talep olarak gider (gemi verisini yalnız gemi sunucusu yazar, mimari
 * belge §8). Kullanıcı onaylamadan hiçbir şey bağlanmaz.
 */

type Message = { tone: 'success' | 'danger'; text: string } | null;

function useErrorText() {
  const { t } = useTranslation();
  return (error: unknown) =>
    t(`voyage.errors.${errorCode(error)}`, { defaultValue: t('voyage.errors.GENERIC') });
}

const byTime = (a: VoyageReport, b: VoyageReport) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/**
 * Details önerisinin listesi (ADR 0157 §4, #2268): sunucunun önerisi (ADR 0083 §3) ve seferin
 * önünde kalan sefersiz raporlar ("baş adaylar") seçili; geminin öteki sefersiz raporları
 * seçimsiz. Tarih sırasıyla; iptal edilmiş rapor yok.
 */
export function suggestionRows(
  voyage: Pick<Voyage, 'id'>,
  voyages: readonly Voyage[],
  suggested: readonly VoyageReport[],
  timeline: readonly VoyageReport[] | null,
  unassigned: readonly VoyageReport[] | null,
): { reports: VoyageReport[]; selected: string[] } {
  const head = timeline
    ? headLinkCandidates(
        timeline.map((r) => ({
          id: r.id,
          kind: r.kind,
          reportedAt: r.reportedAt,
          voyageId: assignedVoyageIdOf(r, voyages),
          voided: r.status === 'void',
        })),
        voyage.id,
      )
    : [];
  const byId = new Map((timeline ?? []).map((r) => [r.id, r]));
  const chosen = new Map<string, VoyageReport>();
  for (const r of [...head.map((id) => byId.get(id)), ...suggested]) {
    if (r && r.status !== 'void') chosen.set(r.id, r);
  }
  const others = (unassigned ?? []).filter((r) => !chosen.has(r.id) && r.status !== 'void');
  const selected = [...chosen.values()].sort(byTime);
  return { reports: [...selected, ...others.sort(byTime)], selected: selected.map((r) => r.id) };
}

/** Voyage Details'te önerilen sefersiz raporlar; öneriler seçili başlar (ADR 0157 §4). */
export function LinkSuggestionsPanel({
  source,
  voyage,
  voyages,
  onChanged,
}: {
  source: VoyageSource;
  voyage: Voyage;
  voyages: readonly Voyage[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const labels = useReportLabels();
  const errorText = useErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.links.suggestions.${key}`, options);
  const [reports, setReports] = useState<VoyageReport[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message>(null);
  // Seferler yalnız okunur: liste her çizimde yeni dizi olsa da öneri yeniden istenmez
  const voyagesRef = useRef(voyages);
  voyagesRef.current = voyages;
  // Öneri okunamadı (ADR 0157 §8): panel sessizce gizlenmez
  const [loadError, setLoadError] = useState<string | null>(null);
  const writable = voyage.status !== 'cancelled' && !voyage.voidedAt;
  const link = source.planner?.link;
  const request = source.requestChange;

  useEffect(() => {
    if (!source.linkSuggestions || !writable) {
      setReports([]);
      return;
    }
    let live = true;
    const optional = (work: Promise<VoyageReport[]> | undefined) =>
      work ? work.catch(() => null) : Promise.resolve(null);
    Promise.all([
      source.linkSuggestions(voyage.id),
      optional(source.allReports?.(voyage.vesselId)),
      optional(source.unassigned?.(voyage.vesselId)),
    ])
      .then(([list, timeline, unassigned]) => {
        if (!live) return;
        const rows = suggestionRows(voyage, voyagesRef.current, list, timeline, unassigned);
        setLoadError(null);
        setReports(rows.reports);
        setSelected(new Set(rows.selected));
      })
      .catch((error: unknown) => {
        if (!live) return;
        setReports([]);
        setLoadError(errorCode(error));
      });
    return () => {
      live = false;
    };
  }, [source, voyage, writable]);

  if (loadError) {
    return (
      <p className={record.note} role="alert">
        {t('voyage.context.suggestionsError', {
          error: t(`voyage.errors.${loadError}`, { defaultValue: t('voyage.errors.GENERIC') }),
        })}
      </p>
    );
  }
  if (!reports || reports.length === 0) {
    return message ? (
      <p className={record.note} role="status">
        {message.text}
      </p>
    ) : null;
  }
  const rows = allReportRows(reports, voyages);
  const ids = reports.map((r) => r.id).filter((id) => selected.has(id));
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const run = async () => {
    setBusy(true);
    setMessage(null);
    try {
      if (link) {
        await link.call(source.planner, voyage.id, ids);
        setMessage({ tone: 'success', text: k('linked', { count: ids.length }) });
      } else if (request) {
        await request.call(source, voyage.vesselId, {
          action: 'link',
          voyageId: voyage.id,
          reportIds: ids,
        });
        setMessage({ tone: 'success', text: k('requested') });
      }
      onChanged();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
    } finally {
      setBusy(false);
    }
  };
  const all = ids.length === reports.length;

  return (
    <Panel
      title={k('title')}
      icon={<IconLink size={20} stroke={1.8} />}
      flush
      extra={<StatusBadge tone="info">{String(reports.length)}</StatusBadge>}
    >
      <p className={classes.lead}>{k('description')}</p>
      <section
        className={`${table.tableWrap} ${record.tableBox}`}
        aria-label={k('title')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={`${table.table} ${record.table}`}>
          <thead>
            <tr>
              <th scope="col" className={record.colNo}>
                <Checkbox
                  aria-label={k('selectAll')}
                  checked={all}
                  indeterminate={!all && ids.length > 0}
                  onChange={() => setSelected(all ? new Set() : new Set(reports.map((r) => r.id)))}
                />
              </th>
              <th scope="col" className={record.colDate}>
                {t('voyage.record.columns.date')}
              </th>
              <th scope="col">{t('voyage.record.columns.report')}</th>
              <th scope="col">{t('voyage.record.columns.location')}</th>
              <th scope="col" className={record.colStatus}>
                {t('voyage.allReports.columns.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const type = labels.typeLabel(row.report.kind, row.type);
              return (
                <tr key={row.report.id}>
                  <td className={record.colNo}>
                    <Checkbox
                      aria-label={k('select', { no: row.report.reportNo || type })}
                      checked={selected.has(row.report.id)}
                      onChange={() => toggle(row.report.id)}
                    />
                  </td>
                  <td>{formatDateTime(row.report.reportedAt, 0)}</td>
                  <td className={record.ellipsis} title={type}>
                    {row.report.reportNo ? `${type} · ${row.report.reportNo}` : type}
                  </td>
                  <td className={record.ellipsis}>{labels.locationLabel(row.location)}</td>
                  <td>
                    <StatusBadge tone={row.status === 'draft' ? 'warning' : 'success'}>
                      {labels.statusLabel(row.status)}
                    </StatusBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <div className={classes.actions}>
        {message ? (
          <p
            className={noon.message}
            data-tone={message.tone}
            role={message.tone === 'danger' ? 'alert' : 'status'}
          >
            {message.text}
          </p>
        ) : (
          <span />
        )}
        {link || request ? (
          <Button
            leftSection={link ? <IconLink size={16} /> : <IconMessage2Share size={16} />}
            loading={busy}
            disabled={ids.length === 0}
            onClick={() => void run()}
          >
            {link ? k('link', { count: ids.length }) : k('request', { count: ids.length })}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}

const REQUEST_TONE: Record<VoyageRequest['status'], StatusTone> = {
  pending: 'warning',
  applied: 'success',
  rejected: 'danger',
};

/**
 * Ofisin sefer talepleri (ADR 0083 §5): ofiste durumları (bekliyor, uygulandı, reddedildi +
 * gerekçe); gemide yetkili kişi uygular ya da gerekçeyle reddeder. Talep yoksa kart çizilmez.
 */
export function VoyageRequestsPanel({
  source,
  vesselId,
  voyages,
  onChanged,
}: {
  source: VoyageSource;
  vesselId: string | null;
  voyages: readonly Voyage[];
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.links.requests.${key}`, options);
  const [requests, setRequests] = useState<VoyageRequest[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<Message>(null);
  const [rejecting, setRejecting] = useState<VoyageRequest | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!vesselId || !source.requests) return setRequests([]);
    try {
      setRequests(await source.requests(vesselId));
    } catch {
      setRequests([]);
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  if (requests.length === 0) return null;
  const planner = source.planner;
  const voyageNo = (request: VoyageRequest) =>
    request.voyage?.voyageNo ??
    voyages.find((v) => v.id === (request.voyageId ?? request.resultVoyageId))?.voyageNo ??
    k('unknownVoyage');
  const decide = async (request: VoyageRequest, work: () => Promise<unknown>, done: string) => {
    setBusy(request.id);
    setMessage(null);
    try {
      await work();
      setMessage({ tone: 'success', text: k(done) });
      setRejecting(null);
      setReason('');
      await load();
      onChanged?.();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel title={k('title')} icon={<IconMessage2Share size={20} stroke={1.8} />}>
      <p className={classes.lead}>{k('description')}</p>
      <ul className={classes.list}>
        {requests.map((request) => (
          <li key={request.id} className={classes.item}>
            <div className={classes.itemHead}>
              <strong>
                {k(`action.${request.action}`, {
                  no: voyageNo(request),
                  count: request.reportIds.length,
                })}
              </strong>
              <StatusBadge tone={REQUEST_TONE[request.status]}>
                {k(`status.${request.status}`)}
              </StatusBadge>
            </div>
            <p className={classes.meta}>
              {k('by', {
                name: request.createdByName ?? '—',
                at: `${formatDateTime(request.createdAt, 0)} UTC`,
              })}
            </p>
            {request.note ? <p className={classes.text}>{request.note}</p> : null}
            {request.decidedAt ? (
              <p className={classes.meta}>
                {k('decided', {
                  name: request.decidedByName ?? '—',
                  at: `${formatDateTime(request.decidedAt, 0)} UTC`,
                })}
              </p>
            ) : null}
            {request.reason ? (
              <p className={classes.text} data-tone="danger">
                {request.reason}
              </p>
            ) : null}
            {request.can?.decide && planner?.acceptRequest && planner.rejectRequest ? (
              <div className={classes.buttons}>
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconX size={14} />}
                  disabled={busy !== null}
                  onClick={() => setRejecting(request)}
                >
                  {k('reject')}
                </Button>
                <Button
                  size="xs"
                  leftSection={<IconCheck size={14} />}
                  loading={busy === request.id}
                  disabled={busy !== null && busy !== request.id}
                  onClick={() =>
                    void decide(
                      request,
                      () => planner.acceptRequest?.(request.id) ?? Promise.resolve(),
                      'applied',
                    )
                  }
                >
                  {k('accept')}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {message ? (
        <p
          className={noon.message}
          data-tone={message.tone}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
      <Modal
        opened={rejecting !== null}
        onClose={() => {
          setRejecting(null);
          setReason('');
        }}
        title={k('rejectTitle')}
        closeButtonProps={{ 'aria-label': k('cancel') }}
      >
        <Textarea
          label={k('reason')}
          description={k('reasonHint')}
          required
          value={reason}
          maxLength={1000}
          autosize
          minRows={2}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        <div className={noon.dialogActions}>
          <Button variant="default" onClick={() => setRejecting(null)}>
            {k('cancel')}
          </Button>
          <Button
            color="red"
            loading={busy !== null}
            disabled={!reason.trim()}
            onClick={() =>
              rejecting &&
              void decide(
                rejecting,
                () => planner?.rejectRequest?.(rejecting.id, reason.trim()) ?? Promise.resolve(),
                'rejectedDone',
              )
            }
          >
            {k('reject')}
          </Button>
        </div>
      </Modal>
    </Panel>
  );
}

/** Voyage Details'in bağlı rapor satırında "Unlink" (gemide çözer, ofiste talep gönderir). */
export function useUnlink(source: VoyageSource, voyage: Voyage, onChanged: () => void) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [message, setMessage] = useState<Message>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const unlink = source.planner?.unlink;
  const request = source.requestChange;
  const writable = voyage.status !== 'cancelled' && !voyage.voidedAt && !!(unlink || request);
  const run = async (report: VoyageReport) => {
    setBusy(report.id);
    setMessage(null);
    try {
      if (unlink) {
        await unlink.call(source.planner, voyage.id, [report.id]);
        setMessage({ tone: 'success', text: t('voyage.links.linked.unlinked') });
      } else if (request) {
        await request.call(source, voyage.vesselId, {
          action: 'unlink',
          voyageId: voyage.id,
          reportIds: [report.id],
        });
        setMessage({ tone: 'success', text: t('voyage.links.linked.unlinkRequested') });
      }
      onChanged();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
    } finally {
      setBusy(null);
    }
  };
  return { writable, office: !unlink, message, busy, run };
}
